import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getVideoById, videos } from '@/mocks/videos';

export default function VideoDetail() {
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(id || '');

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-10 bg-background-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-500 text-sm">影片未找到</p>
            <Link to="/category" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600">
              返回影片列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.id !== video.id && v.category === video.category)
    .slice(0, 4);

  const videoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: video.title,
    description: video.description,
    image: video.coverImage,
    dateCreated: video.releaseDate,
    director: {
      '@type': 'Person',
      name: video.director,
    },
    actor: video.cast.map((name) => ({
      '@type': 'Person',
      name,
    })),
    url: `https://www.madoujipin.com/video/${video.id}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(videoJsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-10 md:pb-14 bg-background-50">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '分类浏览', path: '/category' },
              { label: video.category, path: `/category?cat=${encodeURIComponent(video.category)}` },
              { label: video.title },
            ]}
          />

          <div className="mt-6 md:mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-1">
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={video.coverImage}
                  alt={video.title}
                  title={`${video.title} - 麻豆精品影视网`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 left-3 px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded">
                  {video.quality}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {video.title}
              </h1>
              
              <div className="flex items-center gap-3 mt-4 flex-wrap">
                <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded whitespace-nowrap">
                  {video.category}
                </span>
                {video.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-5 flex items-center gap-4 md:gap-6 text-sm text-foreground-600 flex-wrap">
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500"></i>
                  </div>
                  <strong className="text-foreground-900">{video.rating}</strong>
                </span>
                <span>{video.year}</span>
                <span>{video.episodeCount}</span>
                <span>{video.duration}/集</span>
                <span>{video.views}次播放</span>
              </div>

              <div className="mt-6 bg-background-100 rounded-lg p-4 md:p-5">
                <h3 className="text-sm font-semibold text-foreground-800 mb-2">影片简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-foreground-400">导演：</span>
                  <span className="text-foreground-700">{video.director}</span>
                </div>
                <div>
                  <span className="text-foreground-400">上映日期：</span>
                  <span className="text-foreground-700">{video.releaseDate}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-foreground-400">主演：</span>
                  <span className="text-foreground-700">{video.cast.join('、')}</span>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button className="px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm flex-1 sm:flex-none text-center">
                  <i className="ri-play-fill mr-2"></i>
                  立即播放
                </button>
                <button className="px-8 py-3 bg-background-200 text-foreground-700 font-medium rounded-md hover:bg-background-300 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm flex-1 sm:flex-none text-center">
                  <i className="ri-heart-line mr-2"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          {relatedVideos.length > 0 && (
            <section className="mt-12 md:mt-16">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-5">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-5">
                {relatedVideos.map((rv) => (
                  <Link
                    key={rv.id}
                    to={`/video/${rv.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                      <img
                        src={rv.coverImage}
                        alt={rv.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2 px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded">
                        {rv.quality}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {rv.title}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1 text-xs text-foreground-400">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                      </div>
                      <span>{rv.rating}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}